import Modal from "components/modal/Modal";
import { useIsMobile } from "hooks/use-is-mobile";
import { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from "react-router";

import { getFavoriteCustomerId } from "common/customer/getFavoriteCustomer";
import {
  clearIntendedPath,
  getIntendedPath,
  hasPerformedInitialRedirect,
  logoutUser,
  markInitialRedirectAsPerformed
} from "common/MetaActions";
import Admin from "views/admin/containers/Admin";
import AllCustomers from "views/customers/allCustomers/containers/AllCustomers";
import Customers from "views/customers/Customers";
import { Documents } from "views/customers/documents/containers/Documents";
import MyCustomers from "views/customers/myCustomers/containers/MyCustomers";
import CustomerSettings from "views/customers/settings/containers/CustomerSettings";
import Dashboard from "views/dashboard/containers/Dashboard";
import DocumentDelivery from "views/delivery/containers/DocumentDelivery";
import { DocumentDeliveryTable } from "views/delivery/containers/DocumentDeliveryTable";
import { DocumentDeliveryTableWithOptions } from "views/delivery/containers/DocumentDeliveryTableWithOptions";
import { DocumentDeliveryUpload } from "views/delivery/containers/DocumentDeliveryUpload";
import DocumentArchive from "views/documents/containers/DocumentArchive";
import Footer from "views/footer/Footer";
import Header from "views/header/Header";
import InboxPreview from "views/inbox/components/Inbox/inboxPreview";
import InboxSidebar from "views/inbox/components/Inbox/InboxSidebar";
import MessageEditor from "views/inbox/components/Inbox/main/messageEditor";
import Inbox from "views/inbox/containers/inbox/inbox";
import Inventory from "views/inventory/containers/Inventory";
import Invoices from "views/invoices/containers/Invoices";
import CustomerServerSettings from "views/settings/containers/CustomerServerSettings";
import Unauthorized from "views/unauthorized/Unauthorized";
import Wizard from "views/wizard/containers/Wizard";

import { PrimaryButton } from "components/button/Button";
import { Loader } from "components/loader/Loader";
import ProtectedRoute from "helpers/inbox/protectedRoute";
import { useRootStore } from "store";
import CustomerInformationHeader from "../components/CustomerInformationHeader";
import CustomerLayout from "./CustomerLayout";
import "./Layout.scss";
import { usePermission } from "hooks/use-permissions";

function FallbackRedirect() {
  const { myCustomers } = useRootStore(
    (state) => state.customers
  );
  const { user } = useRootStore((state) => state);
  const isExternal = user.userInfo?.type === "External";

  if (!user.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!isExternal) {
    return <Navigate to="/customers/my" replace />;
  }

  if (myCustomers.isLoading) {
    return <Loader />;
  }

  const favoriteId = getFavoriteCustomerId(user.userInfo?.id);
  const resolvedId =
    favoriteId ?? myCustomers.unfilteredCustomers[0]?.customerId;

  return (
    <Navigate
      to={resolvedId ? `/customers/${resolvedId}/dashboard` : "/unauthorized"}
      replace
    />
  );
}

function ExternalUserRedirect() {
  const { myCustomers, workingCustomer: customer } = useRootStore((state) => state.customers);
  const { user } = useRootStore((state) => state);

  if (myCustomers.isLoading || customer.isLoading) {
    return <Loader />;
  }

  // Customers haven't been fetched yet — wait (same condition as Layout's fetchCustomers effect)
  const willFetchCustomers =
    user.isAuthenticated &&
    !customer.customerId &&
    myCustomers.unfilteredCustomers.length === 0;

  if (willFetchCustomers) {
    return <Loader />;
  }

  const favoriteId = getFavoriteCustomerId(user.userInfo?.id);
  const resolvedId = favoriteId ?? myCustomers.unfilteredCustomers[0]?.customerId;

  return <Navigate to={resolvedId ? `/customers/${resolvedId}/dashboard` : "/unauthorized"} replace />;
}

export default function Layout() {
  const {
    fetchCustomers,
    myCustomers,
    workingCustomer: customer
  } = useRootStore((state) => state.customers);
  const { unfilteredCustomers: customerList } = myCustomers;
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useRootStore((state) => state);
  const { uploadedDocuments, isLoadingUploadedDocuments } = useRootStore(
    (state) => state.delivery
  );
  const { getSettings } = useRootStore((state) => state.customerServerSettings);

  const isExternalUser = usePermission("isExternal");
  const canViewInvoiceReport = usePermission("canViewInvoiceReport");
  const { sessionExpired, setSessionExpired } = user;
  const [redirectPerformed, setRedirectPerformed] = useState(
    hasPerformedInitialRedirect()
  );

  const mobileInboxRoutes = (
    <Route
      path="inbox"
      element={
        <ProtectedRoute
          redirectTo="my"
          accessCondition={(inboxType) =>
            !(isExternalUser && inboxType === "all")
          }
        >
          <Inbox />
        </ProtectedRoute>
      }
    >
      <Route index element={<Navigate to="my" replace />} />
      <Route path=":inboxType" element={<InboxSidebar />} />
      <Route path=":inboxType/threads/:threadId" element={<InboxPreview />} />
      <Route path=":inboxType/messages/:messageId" element={<InboxPreview />} />
      <Route path=":inboxType/new" element={<MessageEditor />} />
    </Route>
  );

  const desktopInboxRoutes = (
    <Route
      path="inbox"
      element={
        <ProtectedRoute
          redirectTo="my"
          accessCondition={(inboxType) =>
            !(isExternalUser && inboxType === "all")
          }
        >
          <Inbox />
        </ProtectedRoute>
      }
    >
      <Route index element={<Navigate to="my" replace />} />
      <Route path=":inboxType">
        <Route path="threads/:threadId" element={<InboxPreview />} />
        <Route path="messages/:messageId" element={<InboxPreview />} />
        <Route path="new" element={<MessageEditor />} />
        <Route index element={<InboxPreview />} />
      </Route>
    </Route>
  );

  useEffect(() => {
    if (isExternalUser) {
      getSettings();
    }
    if (
      user.isAuthenticated &&
      !customer.isLoading &&
      !customer.customerId &&
      customerList.length === 0
    ) {
      fetchCustomers(isExternalUser);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-redirect to intended path or default location
  useEffect(() => {
    // Only run for authenticated users
    if (!user.isAuthenticated) {
      return;
    }

    // Skip if we've already performed the initial redirect this session
    if (hasPerformedInitialRedirect()) {
      return;
    }

    const isSafeInternalPath = (path: string): boolean => {
      if (typeof path !== "string") {
        return false;
      }
      const trimmed = path.trim();
      if (!trimmed.startsWith("/")) {
        return false;
      }
      const lower = trimmed.toLowerCase();
      if (
        lower.startsWith("http://") ||
        lower.startsWith("https://") ||
        lower.startsWith("//") ||
        lower.startsWith("data:")
      ) {
        return false;
      }
      return true;
    };

    // PRIORITY 1: Check for intended path (highest priority)
    const intendedPath = getIntendedPath();
    if (intendedPath) {
      // Clear stored intended path so it does not get reused
      clearIntendedPath();

      if (isSafeInternalPath(intendedPath)) {
        let adjustedPath = isExternalUser
          ? intendedPath.replaceAll("/inbox/all", "/inbox/my")
          : intendedPath;

        if (!isSafeInternalPath(adjustedPath)) {
          adjustedPath = "/";
        }

        navigate(adjustedPath, { replace: true });
        markInitialRedirectAsPerformed();
        setRedirectPerformed(true);
        return;
      }

      // If intended path was unsafe, mark as performed anyway
      // (don't attempt fallback redirect when there was an explicit intended path)
      markInitialRedirectAsPerformed();
      setRedirectPerformed(true);
      return;
    }

    // PRIORITY 2: Fallback redirect (no intended path or unsafe intended path)
    if (isExternalUser) {
      // CRITICAL: Wait for customer list to finish loading
      if (myCustomers.isLoading) {
        return; // Exit early, will re-run when loading completes
      }

      // Check if customer fetch will be triggered (same conditions as first useEffect)
      // If these conditions are true, the fetch hasn't been attempted yet, so wait
      const willFetchCustomers =
        user.isAuthenticated &&
        !customer.isLoading &&
        !customer.customerId &&
        customerList.length === 0;

      if (willFetchCustomers) {
        return; // Wait for fetch to be triggered and complete
      }

      // Customer list has finished loading - determine destination
      const favoriteCustomerId = getFavoriteCustomerId(user.userInfo?.id);

      if (favoriteCustomerId) {
        navigate(`/customers/${favoriteCustomerId}/dashboard`, {
          replace: true
        });
        markInitialRedirectAsPerformed();
        setRedirectPerformed(true);
        return;
      }

      if (customerList.length > 0) {
        navigate(`/customers/${customerList[0].customerId}/dashboard`, {
          replace: true
        });
        markInitialRedirectAsPerformed();
        setRedirectPerformed(true);
        return;
      }

      // Empty customer list - stay at /customers/my (already landed there via routing)
      markInitialRedirectAsPerformed();
      setRedirectPerformed(true);
    } else {
      // Internal user - redirect to /customers/my immediately
      navigate(`/customers/my`, { replace: true });
      markInitialRedirectAsPerformed();
      setRedirectPerformed(true);
    }
  }, [
    user.isAuthenticated,
    isExternalUser,
    customerList,
    myCustomers.isLoading,
    customer.isLoading,
    customer.customerId,
    navigate,
    user.userInfo?.id
  ]);

  const baseClassName =
    location.pathname.includes("inventory") ||
    (location.pathname.includes("invoices") && canViewInvoiceReport)
      ? "invoiceWrapper"
      : "contentWrapper";
  // When session expires, for now, we just want to close the Session-expires modal
  // and not redirect to logout, because we want to allow the user to copy information that they were working on.
  const handleSessionExpired = () => {
    setSessionExpired(false);
  };

  const contentBoxedClassName =
    location.pathname.includes("inbox") ||
      location.pathname.includes("unauthorized")
      ? "content-boxed content-boxed-inbox"
      : "content-boxed";

  const handleLogout = () => {
    logoutUser();
    window.location.href = "/";
  };
  const shouldShowCustomerInfo =
    location.pathname !== "/" &&
    !location.pathname.startsWith("/customers/my") &&
    !location.pathname.startsWith("/customers/all") &&
    !location.pathname.startsWith("/admin") &&
    !location.pathname.startsWith("/unauthorized") &&
    //!location.pathname.startsWith("/settings") && @TODO: Decide if we want to show customer info in settings
    !location.pathname.startsWith("/wizard");

  if (user.isAuthenticated && !redirectPerformed) {
    return (
      <div className="main-layout">
        <Header />
        <div className="loader-container">
          <Loader />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Header />
      {shouldShowCustomerInfo && (
        <div className="customer-information-header-container">
          <CustomerInformationHeader />
        </div>
      )}

      <div className="content-with-footer" id="content-with-footer">
        <div id="main-content" className={baseClassName}>
          <div className={contentBoxedClassName}>
            <Routes>
              {/* Customer management routes (no customerId) */}
              <Route path="/customers" element={<Customers />}>
                <Route
                  path="all"
                  element={
                    isExternalUser ? <ExternalUserRedirect /> : (
                      <ProtectedRoute permission="canViewAllCustomers" redirectTo="/customers/my">
                        <AllCustomers />
                      </ProtectedRoute>
                    )
                  }
                />
                {canViewInvoiceReport && (
                  <Route path="invoices" element={<Invoices />} />
                )}
                <Route path="my" element={isExternalUser ? <ExternalUserRedirect /> : <MyCustomers />} />
                <Route index element={<Navigate to="my" replace />} />
              </Route>

              {/* Customer-specific routes (require customerId) */}
              <Route path="/customers/:customerId" element={<CustomerLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />

                {/* Inbox routes - mobile vs desktop */}
                {isMobile ? mobileInboxRoutes : desktopInboxRoutes}

                <Route path="delivery" element={<DocumentDelivery />}>
                  <Route
                    index
                    element={
                      <Navigate
                        to={!isExternalUser ? "new" : "upload"}
                        replace
                      />
                    }
                  />
                  {/* External-only routes */}
                  <Route
                    path="upload"
                    element={
                      <ProtectedRoute
                        redirectTo="delivery/new"
                        permission="canViewDeliveryUpload"
                      >
                        <DocumentDeliveryUpload />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="uploaded"
                    element={
                      <ProtectedRoute
                        redirectTo="delivery/new"
                        permission="canViewDeliveryUploaded"
                      >
                        <DocumentDeliveryTable />
                      </ProtectedRoute>
                    }
                  />
                  {/* Internal-only routes */}
                  <Route
                    path="handled"
                    element={
                      <ProtectedRoute
                        redirectTo="delivery/upload"
                        permission="canViewDeliveryHandled"
                      >
                        <DocumentDeliveryTableWithOptions
                          files={uploadedDocuments.filter((d) => d.handled)}
                          isLoading={isLoadingUploadedDocuments}
                          title="Låsta"
                        />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="new"
                    element={
                      <ProtectedRoute
                        redirectTo="delivery/upload"
                        permission="canViewDeliveryNew"
                      >
                        <DocumentDeliveryTableWithOptions
                          files={uploadedDocuments.filter((d) => !d.handled)}
                          isLoading={isLoadingUploadedDocuments}
                          title="Nya leveranser"
                        />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                <Route
                  path="documents"
                  element={
                    <ProtectedRoute
                      redirectTo="dashboard"
                      permission="canViewDocuments"
                    >
                      <Documents />
                    </ProtectedRoute>
                  }
                />
                <Route path="inventory" element={<Inventory />} />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute
                      redirectTo="dashboard"
                      permission="canViewCustomerSettings"
                    >
                      <CustomerSettings />
                    </ProtectedRoute>
                  }
                />
                <Route path="archive" element={<DocumentArchive />} />
              </Route>

              {/* Special routes */}
              <Route
                path="/wizard/:id"
                element={
                  <ProtectedRoute permission="canViewWizard" redirectTo="/customers/my">
                    <Wizard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute permission="canViewAdminPanel" redirectTo="/customers/my">
                    <Admin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute permission="canViewCustomerSettings" redirectTo="/customers/my">
                    <CustomerServerSettings />
                  </ProtectedRoute>
                }
              />
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="*" element={<FallbackRedirect />} />
            </Routes>
          </div>
        </div>
        <Footer />
      </div>

      {sessionExpired && (
        <Modal
          size="xs"
          modalTitle="Session expired"
          onDismiss={handleSessionExpired}
        >
          <div className="modalText">
            <h5>
              Din session har gått ut. För att starta en ny session behöver du
              logga in på nytt.
            </h5>
            <div className="d-flex justify-content-end">
              <PrimaryButton size="md" onClick={handleLogout} type="submit">
                Logga ut
              </PrimaryButton>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
